import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from "@/components/ui/button";
import { UsersIcon, ClipboardDocumentListIcon, CurrencyDollarIcon, ClockIcon, BriefcaseIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useI18n } from '@/hooks/useI18n';

export default function PeopleIndexPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const routerState = useRouterState();
    const { t } = useI18n();

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">{t('people.title', 'common')}</h1>
                    <p className="text-muted-foreground">
                        {t('people.subtitle', 'common')}
                    </p>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                    <div className="bg-white p-6 rounded-lg border hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <UsersIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{t('people.employees', 'common')}</h3>
                                <p className="text-muted-foreground">
                                    {t('people.employeesDescription', 'common')}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <Button
                                onClick={() => navigate({ to: '/people/employees'})}
                                className="w-full"
                            >
                                {t('people.manageEmployees', 'common')}
                            </Button>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-indigo-100 rounded-lg">
                                <UserGroupIcon className="h-6 w-6 text-indigo-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{t('people.groups', 'common')}</h3>
                                <p className="text-muted-foreground">
                                    {t('people.groupsDescription', 'common')}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <Button
                                onClick={() => navigate({ to: '/people/groups'})}
                                className="w-full"
                                variant="outline"
                            >
                                {t('people.manageGroups', 'common')}
                            </Button>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <ClipboardDocumentListIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{t('people.contracts', 'common')}</h3>
                                <p className="text-muted-foreground">
                                    {t('people.contractsDescription', 'common')}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <Button
                                onClick={() => navigate({ to: '/people/contracts'})}
                                className="w-full"
                                variant="outline"
                            >
                                {t('people.manageContracts', 'common')}
                            </Button>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{t('people.peopleCosts', 'common')}</h3>
                                <p className="text-muted-foreground">
                                    {t('people.peopleCostsDescription', 'common')}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <Button
                                onClick={() => navigate({ to: '/people/costs'})}
                                className="w-full"
                                variant="outline"
                            >
                                {t('people.managePeopleCosts', 'common')}
                            </Button>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-orange-100 rounded-lg">
                                <ClockIcon className="h-6 w-6 text-orange-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{t('people.timesheets', 'common')}</h3>
                                <p className="text-muted-foreground">
                                    {t('people.timesheetsDescription', 'common')}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <Button
                                onClick={() => navigate({ to: '/people/timesheets'})}
                                className="w-full"
                                variant="outline"
                            >
                                {t('people.viewTimesheets', 'common')}
                            </Button>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-indigo-100 rounded-lg">
                                <BriefcaseIcon className="h-6 w-6 text-indigo-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{t('people.jobs', 'common')}</h3>
                                <p className="text-muted-foreground">
                                    {t('people.jobsDescription', 'common')}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <Button
                                onClick={() => navigate({ to: '/people/jobs' as any})}
                                className="w-full"
                                variant="outline"
                            >
                                {t('people.manageJobs', 'common')}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Coming Soon */}
                <div className="bg-gray-50 p-6 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-2">{t('people.comingSoon', 'common')}</h3>
                    <ul className="space-y-2 text-muted-foreground">
                        <li>• {t('people.payrollManagement', 'common')}</li>
                        <li>• {t('people.employeeSelfService', 'common')}</li>
                        <li>• {t('people.payrollReports', 'common')}</li>
                    </ul>
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/people\/')({
  component: PeopleIndexPage,
})
